import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/common/PageHeader';
import { feedbackService } from '../../services';
import { formatDate } from '../../utils/formatters';

const statusStyles = {
  NEW: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-amber-100 text-amber-900',
  RESOLVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
};

const MyFeedback = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchMyFeedback = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await feedbackService.getMyFeedback({ sortBy: 'createdAt', order: 'DESC' });
      const list = resp?.data?.items || resp?.items || resp?.data || resp || [];
      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.response?.data?.message || t('feedback.my.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setDeletingId(id);
    setError('');
    setSuccess('');
    try {
      await feedbackService.deleteMyFeedback(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
      setSuccess(t('feedback.my.deleteSuccess'));
      setConfirmDelete(null);
    } catch (e) {
      setError(e.response?.data?.message || t('feedback.my.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-primary-50 pb-12">
      <PageHeader
        title={t('feedback.my.title')}
        subtitle={t('feedback.my.subtitle')}
        icon={
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h6m-6 8l-4-4V4a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H7z" />
          </svg>
        }
      />

      <div className="max-w-5xl mx-auto px-4">
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-10 flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-gray-600">{t('feedback.my.empty')}</div>
          ) : (
            <div className="divide-y">
              {items.map((fb) => (
                <div key={fb.id} className="p-5 hover:bg-primary-50/40 transition">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-bold text-gray-900 break-words">{fb.subject || t('feedback.my.noSubject')}</p>
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${statusStyles[fb.status] || 'bg-gray-100 text-gray-700'}`}>
                          {fb.status || '—'}
                        </span>
                        <span className="text-xs text-gray-500">{formatDate(fb.createdAt, lang)}</span>
                      </div>
                      <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap break-words">{fb.message}</p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setConfirmDelete(fb)}
                      className="shrink-0 text-red-600 hover:text-red-800 font-semibold text-sm"
                    >
                      {t('actions.delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900">{t('feedback.my.confirmDeleteTitle')}</h3>
            <p className="text-sm text-gray-700 mt-2">{t('feedback.my.confirmDeleteMessage')}</p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setConfirmDelete(null)}
                disabled={deletingId === confirmDelete.id}
              >
                {t('actions.cancel')}
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                onClick={handleDelete}
                disabled={deletingId === confirmDelete.id}
              >
                {deletingId === confirmDelete.id ? t('common.loading') : t('actions.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyFeedback;

